import { currentUserQuery } from "@common/api/users"
import { useChangeWorkPackageStatusMutation } from "@common/api/work-packages"
import { ConfirmDialog } from "@common/components/shared/confirm-dialog"
import { Textarea } from "@common/components/ui/textarea"
import { hasPermission } from "@common/lib/permissions"
import { handleError } from "@common/utils"
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router"
import { useState } from "react"
import useCustomToast from "@/hooks/use-custom-toast"

export const Route = createFileRoute(
  "/_layout/work-orders/$workOrderId/cancel",
)({
  beforeLoad: async ({ context: { queryClient }, params, search }) => {
    const me = await queryClient.ensureQueryData(currentUserQuery())
    if (!hasPermission(me, "work_package:update")) {
      throw redirect({ to: "/work-orders/$workOrderId", params, search })
    }
  },
  component: CancelWorkOrderDialog,
})

function CancelWorkOrderDialog(): React.ReactElement {
  const { workOrderId } = Route.useParams()
  const navigate = useNavigate()
  const showToast = useCustomToast()
  const mutation = useChangeWorkPackageStatusMutation()
  const [reason, setReason] = useState("")

  const close = () =>
    navigate({
      to: "/work-orders/$workOrderId",
      params: { workOrderId },
      search: true,
    })

  const confirm = () =>
    mutation.mutate(
      {
        work_package_id: workOrderId,
        workPackageTransition: { action: "cancel", reason: reason.trim() },
      },
      {
        onSuccess: () => {
          showToast("Cancelled", "Work order cancelled.", "success")
          close()
        },
        onError: (error) => handleError(error, showToast),
      },
    )

  return (
    <ConfirmDialog
      title="Cancel this work order?"
      description="A cancelled work order can't be reopened."
      action="Cancel work order"
      pendingAction="Cancelling…"
      cancelLabel="Keep it"
      pending={mutation.isPending}
      disabled={!reason.trim()}
      onConfirm={confirm}
      onCancel={close}
    >
      <Textarea
        dictation
        aria-label="Reason"
        placeholder="Why is it cancelled?"
        value={reason}
        autoFocus
        onChange={(event) => setReason(event.target.value)}
      />
    </ConfirmDialog>
  )
}
